// Artifact Storage and Audit Trail
// Frontend-only storage using localStorage
// NO backend persistence in this pass

const STORAGE_KEY = 'sales_artifacts';
const AUDIT_LOG_KEY = 'artifact_audit_log';

/**
 * Load all artifacts from localStorage
 */
export function loadArtifacts() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to load artifacts:', error);
    return [];
  }
}

/**
 * Save artifacts to localStorage
 */
export function saveArtifacts(artifacts) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(artifacts));
    return true;
  } catch (error) {
    console.error('Failed to save artifacts:', error);
    return false;
  }
}

/**
 * Add a new artifact
 */
export function addArtifact(artifact) {
  const artifacts = loadArtifacts();
  artifacts.push(artifact);
  saveArtifacts(artifacts);
  logAuditEvent('ARTIFACT_CREATED', artifact);
  return artifact;
}

/**
 * Update an existing artifact (only if editable)
 */
export function updateArtifact(artifactId, updates) {
  const artifacts = loadArtifacts();
  const index = artifacts.findIndex(a => a.id === artifactId);
  
  if (index === -1) {
    throw new Error(`Artifact ${artifactId} not found`);
  }

  const artifact = artifacts[index];
  
  // Enforce immutability rules
  if (artifact.readonly) {
    throw new Error(`Artifact ${artifactId} is read-only`);
  }
  
  if (artifact.state !== 'draft') {
    throw new Error(`Artifact ${artifactId} is not in draft state (current: ${artifact.state})`);
  }

  // Update artifact
  const updatedArtifact = {
    ...artifact,
    data: { ...artifact.data, ...updates },
    updatedAt: new Date().toISOString()
  };

  artifacts[index] = updatedArtifact;
  saveArtifacts(artifacts);
  logAuditEvent('ARTIFACT_UPDATED', updatedArtifact);
  
  return updatedArtifact;
}

/**
 * Change artifact state (e.g., draft → submitted)
 */
export function transitionArtifactState(artifactId, newState, metadata = {}) {
  const artifacts = loadArtifacts();
  const index = artifacts.findIndex(a => a.id === artifactId);
  
  if (index === -1) {
    throw new Error(`Artifact ${artifactId} not found`);
  }

  const artifact = artifacts[index];
  const oldState = artifact.state;

  // Update state
  const updatedArtifact = {
    ...artifact,
    state: newState,
    updatedAt: new Date().toISOString(),
    ...(newState === 'submitted' && { submittedToFinanceAt: new Date().toISOString() }),
    ...(newState === 'approved' && { approvedAt: new Date().toISOString() })
  };

  artifacts[index] = updatedArtifact;
  saveArtifacts(artifacts);
  
  logAuditEvent('ARTIFACT_STATE_TRANSITION', {
    ...updatedArtifact,
    transition: { from: oldState, to: newState },
    metadata
  });
  
  return updatedArtifact;
}

/**
 * Get artifact by ID
 */
export function getArtifactById(artifactId) {
  const artifacts = loadArtifacts();
  return artifacts.find(a => a.id === artifactId) || null;
}

/**
 * Get artifacts by type
 */
export function getArtifactsByType(type) {
  const artifacts = loadArtifacts();
  return artifacts.filter(a => a.type === type);
}

/**
 * Get artifacts by owner
 */
export function getArtifactsByOwner(owner) {
  const artifacts = loadArtifacts();
  return artifacts.filter(a => a.owner === owner);
}

/**
 * Get artifacts by state
 */
export function getArtifactsByState(state) {
  const artifacts = loadArtifacts();
  return artifacts.filter(a => a.state === state);
}

/**
 * Delete artifact (only if in draft state)
 */
export function deleteArtifact(artifactId) {
  const artifacts = loadArtifacts();
  const artifact = artifacts.find(a => a.id === artifactId);
  
  if (!artifact) {
    throw new Error(`Artifact ${artifactId} not found`);
  }
  
  if (artifact.state !== 'draft') {
    throw new Error(`Cannot delete artifact ${artifactId} - not in draft state`);
  }

  const filtered = artifacts.filter(a => a.id !== artifactId);
  saveArtifacts(filtered);
  logAuditEvent('ARTIFACT_DELETED', artifact);
  
  return true;
}

/**
 * Audit logging
 */
export function logAuditEvent(eventType, data) {
  try {
    const log = loadAuditLog();
    log.push({
      timestamp: new Date().toISOString(),
      eventType,
      artifactId: data.id,
      artifactType: data.type,
      owner: data.owner,
      state: data.state,
      data
    });
    localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(log));
  } catch (error) {
    console.error('Failed to log audit event:', error);
  }
}

export function loadAuditLog() {
  try {
    const data = localStorage.getItem(AUDIT_LOG_KEY);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to load audit log:', error);
    return [];
  }
}

export function clearAuditLog() {
  localStorage.removeItem(AUDIT_LOG_KEY);
}

export function clearAllArtifacts() {
  localStorage.removeItem(STORAGE_KEY);
  clearAuditLog();
}
